import { Typography, Grid, Button, Table, Divider, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	ApplicantInfo,
	ApplicantReference,
	BooleanValue,
	Preceptor,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallback, useState } from 'react';
import { ApplicantDetailsTable } from '../ApplicantDetailsTable';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import { INTERVIEW_LANDING, PRECEPTOR_INTERVIEW } from '../../../shared/Routes';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import RightArrowWhite from '../../../assets/images/arrow-right-white.svg';
import { UpdatePreceptorModal } from './UpdatePreceptorModal';
import { useAuth } from '../../../hooks/useAuth';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface LandingPageProps {
	applicantInfo?: ApplicantInfo;
	preceptorReference?: ApplicantReference;
	searchPreceptorByAbhyasiId?: (
		abhyasiId: string
	) => Promise<Preceptor | undefined>;
	updatePreceptorDetails?: (
		preceptorDetails: ApplicantReference
	) => Promise<BooleanValue | undefined>;
}

const SectionHeader = ({ heading }: { heading: string }) => {
	const { t } = useTranslation();
	const { navigateToPage } = useInterviewLandingPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	return (
		<div className="relative flex items-center justify-between">
			{!isMobile && (
				<Button
					type="primary"
					onClick={() => navigateToPage(INTERVIEW_LANDING)}
					icon={<Icon src={LeftArrowWhite} width={'w-4'} height={'h-4'} />}
					className="absolute left-0 font-bold text-sm"
				>
					{t('back')}
				</Button>
			)}
			<Text className="flex-grow text-center font-sans font-semibold text-lg text-secondary-black my-4">
				{heading}
			</Text>
		</div>
	);
};

export const LandingPage = ({
	applicantInfo,
	preceptorReference,
	searchPreceptorByAbhyasiId,
	updatePreceptorDetails,
}: LandingPageProps) => {
	const { t } = useTranslation();
	const { role } = useAuth();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { navigateToPage } = useInterviewLandingPage();
	const [isPreceptorModalOpen, setIsPreceptorModalOpen] = useState(false);
	const showUpdatePreceptorLink = role === UserRole.FUNCTIONARY;

	const handleUpdatePreceptorDetails = useCallback(() => {
		setIsPreceptorModalOpen(true);
	}, []);

	const handleUpdatePreceptorModalClose = () => {
		setIsPreceptorModalOpen(false);
	};

	if (!preceptorReference) {
		return (
			<div className="flex flex-col justify-center items-center gap-5">
				<Text className="flex-grow text-center font-sans font-semibold text-lg text-secondary-black mt-4">
					{t('preceptorContactDetailsAreLoading')}
				</Text>
				<Spin tip="Loading" size="large" />
			</div>
		);
	}

	const preceptorTableColumns = [
		{
			title: t('name'),
			dataIndex: 'name',
			key: 'name',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('idCardNo'),
			dataIndex: 'srcmid',
			key: 'idCardNo',
			width: '15%',
			className: 'applicant-table-row',
		},
		{
			title: t('email'),
			dataIndex: 'email',
			key: 'email',
			width: '20%',
			render: (text: string) => text,
			className: 'applicant-table-row',
		},
		{
			title: t('mobile'),
			dataIndex: 'mobile',
			key: 'mobile',
			width: '15%',
			render: (text: string) => text,
			className: 'applicant-table-row',
		},
	];

	const data = preceptorReference ? [{ key: '1', ...preceptorReference }] : [];

	return (
		<div className="flex flex-col h-full min-h-0">
			<SectionHeader heading={t('preceptorInterview')} />

			<ApplicantDetailsTable applicantInfo={applicantInfo} />

			<div
				className={`flex-grow flex w-full justify-center items-center mt-6 ${
					isMobile ? 'flex-col gap-1' : 'gap-4'
				}`}
			>
				<Text className="font-sans font-semibold text-base text-secondary-black">
					{t('preceptorDetails')}
				</Text>
				{showUpdatePreceptorLink && (
					<a
						className={`underline hover:underline-offset-4 text-primary-blue font-bold ${
							isMobile ? 'mb-2' : ''
						}`}
						onClick={handleUpdatePreceptorDetails}
					>
						{t('clickToUpdate')}
					</a>
				)}
			</div>

			{!isMobile && (
				<div className="w-3/4 mx-auto">
					<Table
						columns={preceptorTableColumns}
						dataSource={data}
						pagination={false}
						className="my-4 text-sm rounded-md border border-solid border-gray-200"
						showHeader
						size="small"
						scroll={{ x: true }}
					/>
				</div>
			)}

			{isMobile && (
				<div className="grid grid-cols-2 gap-4 p-4 bg-gray-100 rounded-lg border border-solid border-gray-200">
					{preceptorTableColumns.map((col) => (
						<div key={col.dataIndex} className="flex flex-col">
							<Text strong>{col.title}</Text>
							<Text>
								{preceptorReference?.[
									col.dataIndex as keyof ApplicantReference
								] || '-'}
							</Text>
						</div>
					))}
				</div>
			)}

			<div className="flex justify-center">
				<div
					className={`flex flex-col items-center bg-white p-4 rounded-lg shadow-md my-4 ${
						!isMobile ? 'w-3/4' : 'w-full'
					}`}
				>
					<Text className="text-base font-semibold text-center mt-2 text-secondary-black">
						{t('setContextPolitely')}
					</Text>
					<Divider className="border border-solid border-gray-200" />
					<div className="font-sans font-semibold text-sm text-gray-650 px-4">
						<p className="font-extrabold">
							{t('dearProposer')}
							{','}
						</p>
						<p>{t('kindlyFollowGuidelinesForPreceptor')}</p>
						<p>
							<b>
								{'1. '}
								{t('schedulingInterviews')}
							</b>
							<div className="ml-4">
								<li>{t('avoidColdCallsToPreceptor')}</li>
								<li>{t('contactPreceptor')}</li>
								<li>{t('videoCalls')}</li>
							</div>
						</p>
						<p>
							<b>
								{'2. '}
								{t('introScript')}
							</b>
							<p className="mt-1">{t('useTheFollowing')}</p>
							<p>
								{'"'}
								{t('preceptorMayITalkToYou')}
								{'"'}
							</p>
						</p>
						<p>
							<b>
								{'3. '}
								{t('documentResponses')}
							</b>
							<div className="ml-4">
								<li>{t('docPreceptorResponse')}</li>
								<li>{t('maintainObjectivity')}</li>
							</div>
						</p>
						<p>{t('ourCollectiveResponsibility')}</p>
					</div>
					<div
						className={`flex ${
							!isMobile ? 'justify-end' : 'justify-center'
						} w-full gap-2 mt-4`}
					>
						<Button
							onClick={() => navigateToPage(INTERVIEW_LANDING)}
							className="font-bold text-sm"
						>
							{t('cancel')}
						</Button>
						<Button
							type="primary"
							onClick={() => navigateToPage(PRECEPTOR_INTERVIEW)}
							className="font-bold text-sm"
						>
							{t('next')}
							<Icon src={RightArrowWhite} width={'w-5'} height={'h-5'} />
						</Button>
					</div>
				</div>
			</div>
			<UpdatePreceptorModal
				open={isPreceptorModalOpen}
				onClose={handleUpdatePreceptorModalClose}
				searchPreceptorByAbhyasiId={searchPreceptorByAbhyasiId}
				updatePreceptorDetails={updatePreceptorDetails}
			/>
		</div>
	);
};
